import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DietPayButton } from '../../components/medical/DietPayButton';
import { Salad, Loader2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';

/** V66 — landing page for the emailed payment link: /learner/diet-payment/:rid */
export default function DietPaymentPage() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [pr, setPr] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => api.get(`/api/medical/payment-requests/${rid}`).then(r => setPr(r.data)).catch(e => {
    const d = e.response?.data?.detailObject || e.response?.data?.detail;
    setErr((typeof d === 'object' ? d?.message : d) || 'This payment request could not be found.');
  });
  useEffect(() => { load(); }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-md mx-auto space-y-3" data-testid="diet-payment-page">
      <button onClick={() => navigate('/learner/nutritionist')} className="text-sm text-muted-foreground flex items-center gap-1" data-testid="diet-payment-back"><ArrowLeft className="h-4 w-4" />Back to consultation</button>
      <Card className="rounded-2xl overflow-hidden">
        <div className="bg-emerald-600 text-white px-4 py-3 flex items-center gap-2"><Salad className="h-5 w-5" /><p className="font-medium text-sm">Payment request from your nutritionist</p></div>
        <CardContent className="pt-5 space-y-3">
          {err && <p className="text-sm text-rose-600" data-testid="diet-payment-error">{err}</p>}
          {!pr && !err && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {pr && (
            <>
              <p className="text-xs text-muted-foreground">From {pr.provider_name || 'your nutritionist'}</p>
              <p className="text-3xl font-semibold" data-testid="diet-payment-amount">{pr.amount_str}</p>
              <p className="text-sm">{pr.label}</p>
              {pr.note && <p className="text-xs text-muted-foreground">{pr.note}</p>}
              <div className="pt-2">
                {pr.status === 'pending' && <DietPayButton requestId={pr.id} amountStr={pr.amount_str} label={pr.label} size="default" className="w-full" onPaid={() => load()} testId="diet-payment-pay-btn" />}
                {pr.status === 'paid' && <Badge className="bg-emerald-600 text-white" data-testid="diet-payment-status-paid"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Paid{pr.paid_at ? ` · ${new Date(pr.paid_at).toLocaleDateString()}` : ''}</Badge>}
                {pr.status === 'cancelled' && <Badge variant="outline" data-testid="diet-payment-status-cancelled"><XCircle className="h-3.5 w-3.5 mr-1" />Cancelled by the nutritionist</Badge>}
                {pr.status === 'expired' && <Badge variant="outline">Expired — ask your nutritionist for a new request</Badge>}
              </div>
              <p className="text-[11px] text-muted-foreground">Payments are processed securely by Razorpay. Your receipt arrives by email.</p>
            </>
          )}
        </CardContent>
      </Card>
      <Button variant="outline" className="w-full" onClick={() => navigate('/learner/billing')} data-testid="diet-payment-billing-link">View my billing history</Button>
    </div>
  );
}
